
import { HEADING_MASK, HEADING_STEPS, headingFromRadians } from "../sim/fixed";
import { Gait } from "../sim/movement";
import { CAMERA_ROTATE_DEG_PER_SECOND } from "../render/camera";
import type { InputSource } from "../net/connection";

export interface Intent {
  heading: number;
  gait: Gait;
}

export interface SteeredWalk {
  /** The frame this walk wants from where the character now stands, or null. */
  intent(fromXMm: number, fromYMm: number, nowMs: number): Intent | null;
  /** Abandon it. A hand on a control is a person who changed their mind. */
  clear(): void;
}

/** Where the character is, in world millimetres. Read per sample rather than
 *  copied: a burst of catch-up frames moves it between two of them. */
export interface WorldPoint {
  readonly xMm: number;
  readonly yMm: number;
}

/** A direction vector in screen space, or null when the control is at rest. */
export interface Vector {
  x: number;
  y: number;
}

export function headingFromVector(vector: Vector): number {
  return headingFromRadians(Math.atan2(-vector.y, vector.x));
}

export function worldHeadingFromScreen(vector: Vector, cameraYawDeg: number): number {
  const yawSteps = Math.round((cameraYawDeg * HEADING_STEPS) / 360);
  return (headingFromVector(vector) - yawSteps) & HEADING_MASK;
}

export const CARDINAL_HEADINGS = {
  east: 0,
  north: HEADING_STEPS / 4,
  west: HEADING_STEPS / 2,
  south: (HEADING_STEPS * 3) / 4,
} as const;

/** Keyboard state, resolved to one intent per sample. */
function isTyping(target: EventTarget | null): boolean {
  const element = target as HTMLElement | null;
  if (element === null || typeof element !== "object") return false;
  const tag = element.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  return element.isContentEditable === true;
}

export class KeyboardInput {
  private readonly held = new Set<string>();

  constructor(target: EventTarget = window) {
    target.addEventListener("keydown", (event) => this.onKey(event as KeyboardEvent, true));
    target.addEventListener("keyup", (event) => this.onKey(event as KeyboardEvent, false));
    // A window that loses focus mid-stride would otherwise keep the key held and
    // the player would walk into the sea while alt-tabbed.
    target.addEventListener("blur", () => this.held.clear());
  }

  private onKey(event: KeyboardEvent, down: boolean): void {
    const code = event.code;
    if (!TRACKED.has(code)) return;
    if (isTyping(event.target)) {
      // Released without being seen pressed, or held while focus moved into a
      // field: either way the key must not stay latched, or the character walks
      // off on their own the moment the box is closed.
      this.held.delete(code);
      return;
    }
    // Arrow keys scroll the page; the game is the page.
    event.preventDefault();
    if (down) this.held.add(code);
    else this.held.delete(code);
  }

  /** Exposed so tests and the touch layer can drive it without DOM events. */
  set(code: string, down: boolean): void {
    if (down) this.held.add(code);
    else this.held.delete(code);
  }

  get cameraRotation(): number {
    const positive = this.held.has("KeyE") ? 1 : 0;
    const negative = this.held.has("KeyQ") ? 1 : 0;
    return positive - negative;
  }

  get cameraZoom(): number {
    const out = this.held.has("KeyR") ? 1 : 0;
    const closer = this.held.has("KeyF") ? 1 : 0;
    return out - closer;
  }

  get cameraPitchHeld(): number {
    const down = this.held.has("KeyG") ? 1 : 0;
    const up = this.held.has("KeyT") ? 1 : 0;
    return down - up;
  }

  /** Space is held. Auto-repeat is not read off the keyboard: the OS repeat rate
   *  is a user preference and would make attack speed a function of the control
   *  panel, so only the held/released edge is taken from here. */
  get attacking(): boolean {
    return this.held.has("Space");
  }

  get nameRevealHeld(): boolean {
    return this.held.has("AltLeft");
  }

  get mouseHintHeld(): boolean {
    return this.held.has("ControlLeft");
  }

  vector(): Vector | null {
    let x = 0;
    let y = 0;
    if (this.held.has("KeyW") || this.held.has("ArrowUp")) y -= 1;
    if (this.held.has("KeyS") || this.held.has("ArrowDown")) y += 1;
    if (this.held.has("KeyA") || this.held.has("ArrowLeft")) x -= 1;
    if (this.held.has("KeyD") || this.held.has("ArrowRight")) x += 1;
    return x === 0 && y === 0 ? null : { x, y };
  }
}

const TRACKED = new Set([
  "KeyW",
  "KeyA",
  "KeyS",
  "KeyD",
  "ArrowUp",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "Space",
  // Camera yaw. Tracked here with the movement keys rather than handled by a
  // one-off listener in `main.ts` so that the blur-clears-everything rule
  // covers them too: a window that loses focus mid-turn would otherwise spin
  // the camera forever.
  "KeyQ",
  "KeyE",
  // Camera zoom and pitch, for `cameraRotation`'s reason: the original holds
  // these too (`game.py:351,353` and `__PressFKey`/`__PressGKey`, `:466-479`,
  // stopped by the release table at `:404-407`).
  "KeyR",
  "KeyF",
  "KeyT",
  "KeyG",
  "AltLeft",
  "ControlLeft",
]);

export const TAP_MAX_MS = 260;
export const TAP_SLOP_PX = 14;

export function isTap(dxPx: number, dyPx: number, elapsedMs: number): boolean {
  return elapsedMs <= TAP_MAX_MS && Math.hypot(dxPx, dyPx) <= TAP_SLOP_PX;
}

export function isMouseTap(dxPx: number, dyPx: number): boolean {
  return Math.hypot(dxPx, dyPx) <= TAP_SLOP_PX;
}

export class PointerTaps {
  private pending: Vector | null = null;

  push(x: number, y: number): void {
    this.pending = { x, y };
  }

  /** The point tapped since the last call, or null. Reading consumes it. */
  take(): Vector | null {
    const tap = this.pending;
    this.pending = null;
    return tap;
  }

  get waiting(): boolean {
    return this.pending !== null;
  }
}

export const JOYSTICK_RADIUS_PX = 56;

export const ROTATE_STRIP_FRACTION = 0.22;
export const ROTATE_STRIP_MIN_PX = 64;
export const ROTATE_STRIP_MAX_PX = 160;

export const ROTATE_DEG_PER_PX = 0.4;

export const CAMERA_PITCH_DEG_PER_SECOND = 90;

export const CAMERA_ZOOM_PER_SECOND = 3;

export const MAX_TURN_STEP_MS = 400;

const WALK_THRESHOLD = 0.55;

/** No 2014 counterpart: this build's own touch surface, not a rule the 2014
 *  client states. Below this fraction of full deflection the stick reports no
 *  direction at all, so a thumb that has not really left centre -- noise a
 *  touch digitiser reports even at rest -- does not register as a heading. */
const DEAD_ZONE = 0.18;

export class TouchInput {
  visible = false;
  originX = 0;
  originY = 0;
  knobX = 0;
  knobY = 0;
  /** True once a finger has been on the canvas. Touch-only controls stay hidden
   *  until then, so a laptop with a touchscreen does not grow a thumb button. */
  everTouched = false;
  readonly taps = new PointerTaps();

  private pointerId: number | null = null;
  private downAtMs = 0;

  /** The finger currently turning the camera, if any. Deliberately a separate
   *  slot from `pointerId`: the two gestures are mutually exclusive by region,
   *  so neither can ever take the other's pointer away mid-drag. */
  private rotatePointerId: number | null = null;
  private rotateLastX = 0;
  private rotateLastY = 0;
  private rotateOriginX = 0;
  private rotateOriginY = 0;
  private rotateDownAtMs = 0;
  /** Whether the pointer in `rotatePointerId` is a mouse that has not yet
   *  travelled past the tap slop -- see `mouseHold`. */
  private mouseStill = false;
  /** Degrees dragged but not yet handed to the camera. Drained once a frame by
   *  `takeRotationDeg`, so a browser that coalesces ten pointermoves into one
   *  frame turns by the same amount as one that delivers them separately. */
  private rotatePendingDeg = 0;
  private rotatePendingPitchDeg = 0;

  constructor(private readonly element: HTMLElement) {
    element.addEventListener("pointerdown", (e) => this.onDown(e), { passive: false });
    element.addEventListener("pointermove", (e) => this.onMove(e), { passive: false });
    element.addEventListener("pointerup", (e) => this.onUp(e));
    element.addEventListener("pointercancel", (e) => this.onUp(e));
  }

  /** How tall the rotation strip is right now, in CSS pixels. Read per gesture
   *  rather than cached: the canvas is resized by every orientation change, and
   *  a strip measured at startup would be the wrong size in landscape. */
  rotateStripHeightPx(): number {
    const height = this.element.clientHeight || 0;
    return Math.min(
      ROTATE_STRIP_MAX_PX,
      Math.max(ROTATE_STRIP_MIN_PX, height * ROTATE_STRIP_FRACTION),
    );
  }

  private tryCapture(pointerId: number): void {
    try {
      this.element.setPointerCapture(pointerId);
    } catch {
      // Nothing to capture; the gesture proceeds uncaptured.
    }
  }

  private onDown(event: PointerEvent): void {
    if (event.pointerType === "mouse") {
      // Hold-and-drag looks around; a click that never travelled is still a
      // select/walk tap, decided on pointerup. Immediate taps.push used to
      // eat every mouse down before rotatePointerId could start.
      if (this.pointerId !== null || this.rotatePointerId !== null) return;
      event.preventDefault();
      this.mouseStill = true;
      this.rotatePointerId = event.pointerId;
      this.rotateLastX = event.clientX;
      this.rotateLastY = event.clientY;
      this.rotateOriginX = event.clientX;
      this.rotateOriginY = event.clientY;
      this.rotateDownAtMs = event.timeStamp;
      this.tryCapture(event.pointerId);
      return;
    }
    // The canvas is `position: fixed` at the top-left of the viewport (see
    // `stack` in `render/glrenderer.ts` and the stylesheet's own rule), so a
    // client coordinate is already a canvas coordinate.
    if (
      this.pointerId === null &&
      this.rotatePointerId === null &&
      event.clientY <= this.rotateStripHeightPx()
    ) {
      // Only when nothing else is down. A rotation finger arriving next to a
      // steering one is the two-pointer case `ZoomControl` has already decided
      // is a pinch, and claiming it here would leave the stick, the zoom and the
      // camera all fighting over the same gesture.
      event.preventDefault();
      this.everTouched = true;
      this.rotatePointerId = event.pointerId;
      this.rotateLastX = event.clientX;
      this.rotateLastY = event.clientY;
      this.rotateOriginX = event.clientX;
      this.rotateOriginY = event.clientY;
      this.rotateDownAtMs = event.timeStamp;
      this.tryCapture(event.pointerId);
      return;
    }
    // A second finger belongs to the pinch even when the first one started in
    // the rotation strip. Without checking the rotation slot here, that
    // second finger can materialise a joystick below the strip for a frame
    // before the frame loop notices `zoom.pinching` and releases it.
    if (this.pointerId !== null || this.rotatePointerId !== null) return;
    event.preventDefault();
    this.everTouched = true;
    this.downAtMs = event.timeStamp;
    this.pointerId = event.pointerId;
    this.tryCapture(event.pointerId);
    this.originX = event.clientX;
    this.originY = event.clientY;
    this.knobX = event.clientX;
    this.knobY = event.clientY;
    this.visible = true;
  }

  private onMove(event: PointerEvent): void {
    if (event.pointerId === this.rotatePointerId) {
      event.preventDefault();
      // Drag right, look right: the same sense as E. Drag down, look down:
      // clientY grows downward, and Camera.pitchBy treats positive as more
      // downward pitch.
      this.rotatePendingDeg += (event.clientX - this.rotateLastX) * ROTATE_DEG_PER_PX;
      this.rotatePendingPitchDeg += (event.clientY - this.rotateLastY) * ROTATE_DEG_PER_PX;
      this.rotateLastX = event.clientX;
      this.rotateLastY = event.clientY;
      if (!isMouseTap(event.clientX - this.rotateOriginX, event.clientY - this.rotateOriginY)) {
        this.mouseStill = false;
      }
      return;
    }
    if (event.pointerId !== this.pointerId) return;
    event.preventDefault();
    this.knobX = event.clientX;
    this.knobY = event.clientY;
  }

  private onUp(event: PointerEvent): void {
    if (event.pointerId === this.rotatePointerId) {
      this.rotatePointerId = null;
      this.mouseStill = false;
      const dx = event.clientX - this.rotateOriginX;
      const dy = event.clientY - this.rotateOriginY;
      const wasTap =
        event.type === "pointerup" &&
        (event.pointerType === "mouse"
          ? isMouseTap(dx, dy)
          : isTap(dx, dy, event.timeStamp - this.rotateDownAtMs));
      if (wasTap) {
        this.taps.push(event.clientX, event.clientY);
      }
      return;
    }
    if (event.pointerId !== this.pointerId) return;
    this.pointerId = null;
    this.visible = false;
    // A thumb that went down and came back up without travelling was pointing at
    // something, not steering. `pointercancel` reaches here too and is treated as
    // a drag: the gesture was taken away from us, so it was not a deliberate tap.
    if (
      event.type === "pointerup" &&
      isTap(event.clientX - this.originX, event.clientY - this.originY, event.timeStamp - this.downAtMs)
    ) {
      this.taps.push(event.clientX, event.clientY);
    }
  }

  release(): void {
    // The rotation finger goes too. A pinch that began from the strip would
    // otherwise keep turning the camera for as long as the zoom lasted.
    this.rotatePendingDeg = 0;
    this.rotatePendingPitchDeg = 0;
    this.rotatePointerId = null;
    this.mouseStill = false;
    if (this.pointerId === null) return;
    this.pointerId = null;
    this.visible = false;
  }

  /** Where a mouse button held down without travelling points now, or null.
   *  `m_isSmtMov` (`PythonPlayerInputMouse.cpp:120-124`): the held left
   *  button presses whatever is under it again every frame (`:376-377`). */
  mouseHold(): Vector | null {
    if (this.rotatePointerId === null || !this.mouseStill) return null;
    return { x: this.rotateLastX, y: this.rotateLastY };
  }

  /** Degrees of yaw dragged since the last call. Reading consumes it, so the
   *  same drag can never be spent twice. */
  takeRotationDeg(): number {
    const degrees = this.rotatePendingDeg;
    this.rotatePendingDeg = 0;
    return degrees;
  }

  takePitchDeg(): number {
    const degrees = this.rotatePendingPitchDeg;
    this.rotatePendingPitchDeg = 0;
    return degrees;
  }

  /** Deflection as a unit-ish vector, or null inside the dead zone. */
  vector(): Vector | null {
    if (!this.visible) return null;
    const dx = this.knobX - this.originX;
    const dy = this.knobY - this.originY;
    const length = Math.hypot(dx, dy);
    if (length < DEAD_ZONE * JOYSTICK_RADIUS_PX) return null;
    return { x: dx, y: dy };
  }

  /** How far the stick is pushed, clamped to 1. Drives the walk/run decision. */
  deflection(): number {
    if (!this.visible) return 0;
    const length = Math.hypot(this.knobX - this.originX, this.knobY - this.originY);
    return Math.min(1, length / JOYSTICK_RADIUS_PX);
  }

  /** Where to draw the knob: clamped to the ring, so it never flies off-screen. */
  knobPosition(): Vector {
    const dx = this.knobX - this.originX;
    const dy = this.knobY - this.originY;
    const length = Math.hypot(dx, dy);
    if (length <= JOYSTICK_RADIUS_PX || length === 0) {
      return { x: this.originX + dx, y: this.originY + dy };
    }
    const scale = JOYSTICK_RADIUS_PX / length;
    return { x: this.originX + dx * scale, y: this.originY + dy * scale };
  }
}

export class PlayerInput implements InputSource {
  private heading = 0;

  constructor(
    private readonly keyboard: KeyboardInput,
    private readonly touch: TouchInput | null = null,
    private readonly cameraYawDeg: () => number = () => 0,
    /** The pending click-to-move, if this client has one. */
    private readonly walk: SteeredWalk | null = null,
    /** Where the character is now. Only ever read when `walk` has a goal. */
    private readonly positionMm: () => WorldPoint = () => ({ xMm: 0, yMm: 0 }),
    private readonly now: () => number = () => performance.now(),
  ) {}

  sample(): Intent {
    const yawDeg = this.cameraYawDeg();
    const stick = this.touch?.vector() ?? null;
    if (stick !== null) {
      this.walk?.clear();
      this.heading = worldHeadingFromScreen(stick, yawDeg);
      const gait = (this.touch?.deflection() ?? 0) < WALK_THRESHOLD ? Gait.WALK : Gait.RUN;
      return { heading: this.heading, gait };
    }

    const keys = this.keyboard.vector();
    if (keys !== null) {
      this.walk?.clear();
      this.heading = worldHeadingFromScreen(keys, yawDeg);
      return { heading: this.heading, gait: Gait.RUN };
    }

    if (this.walk !== null) {
      const here = this.positionMm();
      const steered = this.walk.intent(here.xMm, here.yMm, this.now());
      if (steered !== null) {
        this.heading = steered.heading;
        return steered;
      }
    }

    return { heading: this.heading, gait: Gait.IDLE };
  }

  cameraYawDeltaDeg(elapsedMs: number): number {
    const step = Math.min(MAX_TURN_STEP_MS, Math.max(0, elapsedMs));
    const held = this.keyboard.cameraRotation * CAMERA_ROTATE_DEG_PER_SECOND * (step / 1000);
    return held + (this.touch?.takeRotationDeg() ?? 0);
  }

  cameraPitchDeltaDeg(elapsedMs: number = 0): number {
    const step = Math.min(MAX_TURN_STEP_MS, Math.max(0, elapsedMs));
    const held =
      this.keyboard.cameraPitchHeld * CAMERA_PITCH_DEG_PER_SECOND * (step / 1000);
    return held + (this.touch?.takePitchDeg() ?? 0);
  }

  cameraZoomFactor(elapsedMs: number): number {
    const direction = this.keyboard.cameraZoom;
    if (direction === 0) return 1;
    const step = Math.min(MAX_TURN_STEP_MS, Math.max(0, elapsedMs));
    return Math.exp(direction * CAMERA_ZOOM_PER_SECOND * (step / 1000));
  }
}
